import { Box, makeStyles, Typography } from '@dxb-web-ui-framework/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Csv as CsvIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import { formatAmount } from '@dxb-web-ui-framework/common/utils/Math';
import {
  actionAuthorizationPayrollButtonsRenderer,
  formatAmountCellRenderer,
  payrollStatusCellRenderer,
  textWithIconCellRenderer,
  viewDetailsButtonRenderer,
} from '../../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import { TableGridConfig } from '../../../../content-composer/src/components/Generic/types';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';

const useStyles = makeStyles(theme => ({
  authActionButtonContainer: {
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      right: theme.spacing(3),
      bottom: theme.spacing(1),
    },
  },
  authActionText: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    color: 'white',
    '&:hover': {
      cursor: 'pointer',
      opacity: '0.8',
    },
  },
  reject: {
    backgroundColor: theme.palette.primary.main,
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2.8)}px`,
  },
  approve: {
    backgroundColor: theme.palette.success.main,
  },
  disabledAuthActionText: {
    backgroundColor: `${theme.palette.text.disabled} !important`,
    '&:hover': {
      cursor: 'default',
      opacity: '1',
    },
  },
  typographyValue: {
    fontSize: theme.typography.body2.fontSize,
  },
  tableActionButton: {
    padding: theme.spacing(0),
  },
}));

const useAuthorizationPayrollGridConfig = (
  actionCallback: Function,
  onToggleDrawer: Function,
  onSelectListItem: (rowData: any) => void,
): TableGridConfig => {
  const language = useLanguage();
  const locale = language.cultureName;
  const { t } = useTranslation();
  const styles = useStyles();

  return {
    gridOptions: {
      ariaTitle: t('payrollPage.PayrollTable.UsePayrollGridConfig.GridOptions.AriaTitle'),
      dataTestId: 'Bulk-transfers-table',
      noResultsMsg: t('payrollPage.PayrollTable.UsePayrollGridConfig.GridOptions.NoResultMessage'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    columnDefs: [
      {
        id: 'From',
        field: 'fromAccount',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.From'),
        cellRenderer: (rowData: any, columnDef: any) => (
          <Typography variant='subtitle2'>{rowData[columnDef.field]}</Typography>
        ),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Status',
        field: 'status',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.Status'),
        cellRenderer: payrollStatusCellRenderer,
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'File type',
        field: 'fileType',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.FileType'),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'File name',
        field: 'fileName',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.FileName'),
        cellRenderer: textWithIconCellRenderer(<CsvIcon />),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Amount',
        field: 'amount',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.Amount'),
        columnHeaderCellProps: { align: 'right', colSpan: 1 },
        cellProps: { align: 'right', colSpan: 1 },
        // TODO: handle 'N/A'.
        cellRenderer: (rowData: any, columnDef: any) =>
          rowData.currencyIso !== 'N/A'
            ? formatAmount(rowData[columnDef.field], rowData['currency'], 2, locale)
            : rowData[columnDef.field],
      },
      {
        id: 'Actions',
        field: 'actions',
        label: t('authorizationListPage.useAuthorizationGridConfig.columnDefs.Actions'),
        columnHeaderCellProps: { align: 'center' },
        cellRenderer: actionAuthorizationPayrollButtonsRenderer(actionCallback),
      },
      {
        id: 'View Icon',
        field: 'actions',
        label: '',
        cellProps: { align: 'right' },
        cellRenderer: viewDetailsButtonRenderer(
          onToggleDrawer,
          'payrollFileId',
          false,
          t('payrollPage.payrollDetails.VisibilityIconBtnTitle'),
          t('payrollPage.payrollDetails.VisibilityIconBtnAriaLabel'),
        ),
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'fromAccount',
          value: 'fromAccount',
          valueAreaLabel: 'from',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          valueTransformer: (data: any) => (
            <Box display='flex' flexDirection='column' component='span'>
              <Box mt={0.5} component={'span'} />
              <Typography
                color='textSecondary'
                component='p'
                aria-label={data.fromAccount}
                className={styles.typographyValue}>
                {data.from}
              </Typography>
              <Box mt={3} component={'span'} />
              <Typography
                color='textSecondary'
                component='p'
                aria-label={data.fileName}
                className={styles.typographyValue}>
                {data.fileName}
              </Typography>
            </Box>
          ),
        },
        {
          label: 'amount',
          value: 'status',
          valueAreaLabel: 'status',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelTransformer: (data: any) => (
            <Box display={'flex'} justifyContent={'flex-end'}>
              {data.currencyIso !== 'N/A'
                ? formatAmountCellRenderer(locale, true, 'currency')(data, { field: 'amount' })
                : data.totalAmount}
            </Box>
          ),
          valueTransformer: (data: any) => (
            <Box mt={3} mb={3} display='flex' component='span' justifyContent={'flex-end'}>
              {payrollStatusCellRenderer(data, { field: 'status' })}
            </Box>
          ),
        },
      ],
      listItemOnSelect: onSelectListItem,
      listItemActions: (rowData: any, columnDef: any) => (
        <Box className={styles.authActionButtonContainer}>
          {actionAuthorizationPayrollButtonsRenderer(actionCallback)(rowData, columnDef)}
        </Box>
      ),
    },
  };
};

export { useAuthorizationPayrollGridConfig };
